/* eslint-disable max-lines */
import type { HttpErrorResponse } from '@angular/common/http';
import type { ActivatedRouteSnapshot } from '@angular/router';
import { PRIMARY_OUTLET } from '@angular/router';

import { createSelector } from '@ngrx/store';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { CurrentUserSelectors } from '@core/account-data';
import type { Position } from '@core/http-types';
import type { EntityState } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  createSelectorDeepEqual,
  DEFAULT_SCOPE_NAME,
  getEntityCollectionActions,
  ngrxEvent,
  pipeState,
  routerSelector,
} from '@core/ngrx-legacy';
import type {
  ReportDashboard,
  ReportDashboardApiEntity,
  ReportDashboardComponent,
  ReportDashboardComponentGridPosition,
  ReportDashboardFilters,
  ReportsDashboardCreate,
} from '@core/reports-types';
import { dashboardApiEntityToDashboard } from '@core/reports-util';
import type { AnySymbol } from '@core/symbol-legacy';
import type { Visibility } from '@core/visibility-types';

import { ReportsComponentsSelectors } from '../components/components.store';
import { accountCreatedAt } from '../helpers/account-created-at';
import { safeReportsStateSelector } from '../helpers/safe-selector';
import { sortDashboardsByPosition } from '../helpers/sort-dashboards-by-position';

type ReportsDashboardsEntityCollectionState = EntityState<ReportDashboard>;

const selectDashboardId = ({ id }: { id: number | string }) => id;

const getDashboardsPositions = (dashboards: Array<ReportDashboardApiEntity | ReportDashboard>) =>
  dashboards.map(({ id }, index) => ({ id, position: index + 1 }));

const setMetaPositions = (
  state: ReportsDashboardsEntityCollectionState,
  positions: Position[],
) => ({
  ...state,
  entityMetaMap: {
    ...state.entityMetaMap,
    ...Object.fromEntries(
      positions.map(item => [
        selectDashboardId(item),
        { ...state.entityMetaMap[selectDashboardId(item)], position: item.position },
      ]),
    ),
  },
});

const payloadAdapter = <T extends { data: ReportDashboardApiEntity[] | ReportDashboardApiEntity }>(
  payload: T,
): Omit<T, 'data'> & {
  data: [T['data']] extends [any[]] ? ReportDashboard[] : ReportDashboard;
} => {
  const { data, ...p } = payload;
  if (Array.isArray(data)) {
    return <any>{ ...p, data: data.map(dashboardApiEntityToDashboard) };
  }
  return <any>{ ...p, data: dashboardApiEntityToDashboard(data) };
};

const { actions, selectors, reducer, namespace, initialState } =
  createEntityCollectionStore<ReportDashboard>()('ReportsDashboards', {
    baseSelector: (root): EntityState<ReportDashboard> =>
      safeReportsStateSelector(root, namespace, initialState),
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const {
        createOneOptimistic,
        createOnePessimistic,
        createOnePessimisticFailed,
        createOnePessimisticSuccess,
        createOneOptimisticSuccess,
        updateOnePessimistic,
        updateOnePessimisticSuccess,
        updateOnePessimisticFailed,
        updateOneOptimistic,
        updateOneOptimisticSuccess,
        updateOneOptimisticFailed,
        updateOne,
        removeOneOptimistic,
      } = getEntityCollectionActions(adapter, selectDashboardId);

      return {
        resetAll: state => adapter.resetAll(state),

        fetchCollectionSuccess: (
          state,
          payload: { data: { dashboards: ReportDashboardApiEntity[] } },
        ) =>
          pipeState(
            state,
            state => {
              const converted = payload.data.dashboards.map(dashboardApiEntityToDashboard);

              // preserve componentIds for individually-loaded entities to avoid
              // overwriting data fetched by fetchOne with empty arrays from the collection endpoint
              const merged = converted.map(d => {
                const entityMeta = state.entityMetaMap[d.id];
                const existingEntity = state.entityMap[d.id];

                return entityMeta?.loaded && existingEntity
                  ? { ...d, componentIds: existingEntity.componentIds }
                  : d;
              });

              return adapter.setMany(state, merged);
            },

            next => setMetaPositions(next, getDashboardsPositions(payload.data.dashboards)),
            next =>
              adapter.markAsLoaded(next, {
                entities: [],
                scope: DEFAULT_SCOPE_NAME,
              }),
          ),
        fetchCollectionFailed: (state, error: any) => adapter.setError(state, error),

        fetchOne: (state, payload: { id: number }) =>
          adapter.markAsPending(state, { scope: null, entities: [payload.id] }),

        fetchOneSuccess: (state, payload: { data: ReportDashboardApiEntity }) =>
          pipeState(
            state,
            next => adapter.writeOne(next, dashboardApiEntityToDashboard(payload.data)),
            next => adapter.markAsLoaded(next, { entities: [payload.data.id], scope: null }),
          ),

        fetchOneFailed: (state, payload: { id: number; error: any }) =>
          adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),

        createOneOptimistic: (
          state,
          payload: {
            data: ReportsDashboardCreate;
            correlationId: string;
          },
        ) => createOneOptimistic(state, payload),

        createOneOptimisticSuccess: (
          state,
          payload: {
            data: ReportDashboardApiEntity;
            correlationId: string;
          },
        ) =>
          pipeState(
            state,
            next => createOneOptimisticSuccess(next, payloadAdapter(payload)),
            next => ({
              ...next,
              entityMetaMap: {
                ...next.entityMetaMap,
                [payload.data.id]: {
                  ...next.entityMetaMap[payload.data.id],
                  position: next.scope[DEFAULT_SCOPE_NAME].length + 1,
                },
              },
            }),
          ),

        duplicateOne: (
          state,
          payload: {
            data: ReportsDashboardCreate;
            correlationId: string;
          },
        ) =>
          // duplication works in a pessimistic way, it navigates only after the successful action
          createOnePessimistic(state, payload),

        duplicateOneSuccess: (
          state,
          payload: {
            data: ReportDashboardApiEntity;
            correlationId: string;
          },
        ) => createOnePessimisticSuccess(state, payloadAdapter(payload)),

        duplicateOneFailed: (
          state,
          payload: {
            data: any;
            error: HttpErrorResponse;
            correlationId: string;
          },
        ) =>
          createOnePessimisticFailed(state, {
            ...payload,
          }),

        updateNameAndSymbol: (
          state,
          payload: { data: { id: number; name: string; symbol: AnySymbol | null } },
        ) => updateOnePessimistic(state, { data: payload.data }),

        updateNameAndSymbolSuccess: (state, payload: { data: ReportDashboardApiEntity }) =>
          updateOnePessimisticSuccess(state, payloadAdapter(payload)),

        updateNameAndSymbolFailed: (
          state,
          payload: {
            data: { id: ReportDashboard['id'] };
            error: HttpErrorResponse;
          },
        ) => updateOnePessimisticFailed(state, { ...payload }),

        updateVisibility: ngrxEvent<{
          data: {
            id: number;
            visibility: Visibility;
          };
        }>(),
        updateVisibilitySuccess: (state, payload: { data: ReportDashboardApiEntity }) =>
          updateOnePessimisticSuccess(state, payloadAdapter(payload)),

        updateVisibilityFailed: (
          state,
          payload: {
            data: { id: ReportDashboard['id'] };
            error: HttpErrorResponse;
          },
        ) => updateOnePessimisticFailed(state, payload),

        updateFiltersOptimistic: (
          state,
          payload: {
            correlationId: string;
            kind: 'date' | 'filters' | 'reset';
            data: {
              id: number;
              filters: ReportDashboard['filters'];
              dateFilter: ReportDashboard['dateFilter'];
            };
          },
          // payload has partial component. Update expects full object
        ) => updateOneOptimistic(state, <any>payload),

        updateFiltersLocal: (
          state,
          payload: {
            kind: 'date' | 'filters' | 'reset';
            data: {
              id: number;
              filters: ReportDashboard['filters'];
              dateFilter: ReportDashboard['dateFilter'];
            };
          },
        ) => {
          const update: Partial<ReportDashboard> = payload.data;

          return updateOne(state, {
            data: <ReportDashboard>update, // TODO: should accept Partial<Entity>!
          });
        },

        updateFiltersOptimisticSuccess: (
          state,
          payload: { data: ReportDashboardApiEntity; correlationId: string },
        ) => {
          const ogPayload = payloadAdapter(payload);
          const minifiedChangesPayload = {
            ...ogPayload,
            // update one requires full type, but supports partial type
            data: <any>({
              id: ogPayload.data.id,
              filters: ogPayload.data.filters,
              dateFilter: ogPayload.data.dateFilter,
            } satisfies Partial<ReportDashboard>),
          };
          return updateOneOptimisticSuccess(state, minifiedChangesPayload);
        },

        updateFiltersOptimisticFailed: (
          state,
          payload: {
            data: { id: ReportDashboard['id'] };
            correlationId: string;
            error: HttpErrorResponse;
          },
        ) => updateOneOptimisticFailed(state, payload),

        updatePositionsOptimistic: (state, payload: { data: Position[]; correlationId: string }) =>
          pipeState(state, next => setMetaPositions(next, payload.data)),

        updatePositionsOptimisticSuccess: (
          state,
          payload: { correlationId: string; data: ReportDashboardApiEntity[] },
        ) => pipeState(state, next => setMetaPositions(next, getDashboardsPositions(payload.data))),
        // TODO
        updatePositionsOptimisticFailed: ngrxEvent<{
          error: HttpErrorResponse;
        }>(),

        moveComponent: (
          state,
          payload: {
            componentId: number;
            sourceDashboardId: number;
            targetDashboardId: number;
            localId: string | null;
            grid: ReportDashboardComponentGridPosition;
          },
        ) =>
          pipeState(state, next => {
            const { componentId, sourceDashboardId, targetDashboardId, localId } = payload;
            const sourceDashboard = next.entityMap[sourceDashboardId];
            const targetDashboard = next.entityMap[targetDashboardId];

            return adapter.updateMany(next, [
              {
                ...sourceDashboard,
                componentIds: sourceDashboard?.componentIds.filter(
                  id => id !== componentId && (!localId || localId !== id),
                ),
              },
              {
                ...targetDashboard,
                componentIds: [...targetDashboard!.componentIds, componentId],
              },
            ]);
          }),
        moveComponentSuccess: state => state,
        moveComponentFailed: (
          state,
          payload: {
            componentId: number;
            sourceDashboardId: number;
            targetDashboardId: number;
            error: any;
          },
        ) =>
          pipeState(state, next => {
            const { componentId, sourceDashboardId, targetDashboardId } = payload;
            const sourceDashboard = next.entityMap[sourceDashboardId];
            const targetDashboard = next.entityMap[targetDashboardId];

            return adapter.updateMany(next, [
              {
                ...sourceDashboard,
                componentIds: [...sourceDashboard!.componentIds, componentId],
              },
              {
                ...targetDashboard,
                componentIds: targetDashboard?.componentIds.filter(id => id !== componentId),
              },
            ]);
          }),
        removeOneOptimistic: (state, payload: { dashboard: ReportDashboard }) =>
          removeOneOptimistic(state, { data: payload.dashboard.id }),
      };
    },
  });

const meta = createSelectorDeepEqual(selectors.meta(), metaState => ({
  pending: metaState.isPending,
  loaded: metaState.isLoaded,
  failed: metaState.isFailed,
  error: metaState.error,
}));

const withReferences = () =>
  createSelectorDeepEqual(
    selectors.entities(),
    ReportsComponentsSelectors.entityMap,
    ReportsComponentsSelectors.aliasesMap,
    selectors.entityMetaMap,
    (
      dashboards,
      components,
      componentsAlias,
      metaMap,
    ): Array<
      ReportDashboard & {
        position: number;
        components: ReportDashboardComponent[];
      }
    > =>
      (dashboards || []).map(d => ({
        ...d,
        position: (<any>metaMap[d.id])?.position,
        components: (d.componentIds || []).map(id => components[componentsAlias[id] || id]!),
      })),
  );

const findDashboardRoute = (root: ActivatedRouteSnapshot) => {
  let currentRoute: ActivatedRouteSnapshot | null = root;

  while (currentRoute && currentRoute.outlet === PRIMARY_OUTLET) {
    if (currentRoute.params.dashboardId) {
      return currentRoute;
    } else {
      currentRoute = currentRoute.firstChild;
    }
  }

  return undefined;
};

const selectActiveDashboardRouteId = createSelector(routerSelector, router => {
  const route = findDashboardRoute(router?.state?.root!);
  const dashboardId = route?.params.dashboardId.split(',').pop();
  return dashboardId;
});

const active = createSelector(
  selectors.entityMap,
  selectActiveDashboardRouteId,
  (entities, dashboardId): ReportDashboard | null =>
    dashboardId && dashboardId in entities ? entities[dashboardId] : null,
);

const activeComponentIds = createSelectorDeepEqual(active, active =>
  active ? active.componentIds : [],
);

const activeWithReferences = createSelectorDeepEqual(
  active,
  ReportsComponentsSelectors.entityMap,
  ReportsComponentsSelectors.aliasesMap,
  (dashboard, components, componentsAlias) => {
    if (!dashboard) return null;
    const resolvedComponents = dashboard.componentIds
      .map(id => components[componentsAlias[id] || id]!)
      .filter(c => c !== undefined);
    return {
      ...dashboard,
      componentIds: resolvedComponents.map(c => c.id),
      components: resolvedComponents,
    };
  },
);

const dashboardWithReferencesById = (id: number) =>
  createSelectorDeepEqual(
    selectors.byId(id),
    ReportsComponentsSelectors.entityMap,
    ReportsComponentsSelectors.aliasesMap,
    (dashboard, components, componentsAlias) => {
      if (!dashboard) return null;
      const resolvedComponents = dashboard.componentIds
        .map(id => components[componentsAlias[id] || id]!)
        .filter(c => c !== undefined);
      return {
        ...dashboard,
        componentIds: resolvedComponents.map(c => c.id),
        components: resolvedComponents,
      };
    },
  );

const activeMeta = createSelectorDeepEqual(active, selectors.entityMetaMap, (dashboard, meta) =>
  dashboard ? meta[dashboard.id] : { pending: false, loaded: false, failed: false, error: null },
);

const activeFilters = createSelectorDeepEqual(
  active,
  (dashboard): ReportDashboardFilters => ({
    filters: dashboard?.filters || [],
    dateFilter: dashboard?.dateFilter || {
      dateRange: null,
      dateStart: null,
      dateEnd: null,
    },
  }),
);

const dashboardDataProps = (id: string | number) =>
  createSelectorDeepEqual(
    selectors.byId(id),
    dashboard =>
      <ReportDashboardFilters>{
        filters: dashboard?.filters || [],
        dateFilter: dashboard?.dateFilter || {
          dateRange: null,
          dateStart: null,
          dateEnd: null,
        },
      },
  );

const dashboardsListSorted = createSelectorDeepEqual(
  withReferences(),
  (dashboards): ReportDashboard[] => dashboards.sort(sortDashboardsByPosition),
);

const canManageDashboards = AbilitiesSelectors.hasOne(Ability.ManageReportDashboards);
const canViewDashboards = canManageDashboards;
const canExportData = AbilitiesSelectors.hasOne(Ability.ExportData);
const canViewHiredCandidates = AbilitiesSelectors.hasOne(Ability.ViewHiredCandidates);
const canManageRequisitions = AbilitiesSelectors.hasOne(Ability.ManageRequisitions);

const abilities = createSelectorDeepEqual(
  canManageDashboards,
  canViewDashboards,
  canExportData,
  canViewHiredCandidates,
  canManageRequisitions,
  (
    canManageReportDashboards,
    canViewReportDashboards,
    canExportData,
    canViewHiredCandidates,
    canManageRequisitions,
  ) => ({
    canManageReportDashboards,
    canViewReportDashboards,
    canExportData,
    canViewHiredCandidates,
    canManageRequisitions,
  }),
);

const showIntroInfobox = createSelectorDeepEqual(
  CurrentUserSelectors.data,
  user => !user?.featureDiscovery.newReportsUsed || false,
);

export const ReportsDashboardsSelectors = {
  ...selectors,
  meta,
  active,
  activeComponentIds,
  activeWithReferences,
  dashboardWithReferencesById,
  activeMeta,
  activeFilters,
  dashboardDataProps,
  dashboardsListSorted,
  abilities,
  showIntroInfobox,
  accountCreatedAt,
  canManageDashboards,
};

export {
  actions as ReportsDashboardsActions,
  reducer as ReportsDashboardsReducer,
  namespace as ReportsDashboardsNamespace,
};
